import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { checkSubscriptionAndRedirect } from '../utils/subscriptionHelpers';

function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [hideSignupPassword, setHideSignupPassword] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const mode = searchParams.get('mode');
    setIsLogin(mode !== 'signup');

    // Pre-populate signup password if provided in URL
    const urlSignupPassword = searchParams.get('signupPassword');
    if (urlSignupPassword) {
      setSignupPassword(urlSignupPassword);
      setHideSignupPassword(true);
    }

    // Add token validation
    const validateToken = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch(`${API_BASE_URL}/validate_token`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
          });

          if (response.ok) {
            navigate('/dashboard');
          } else {
            localStorage.removeItem('token');
          }
        } catch (error) {
          console.error('Token validation error:', error);
          localStorage.removeItem('token');
        }
      }
    };

    validateToken();
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isLogin && password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    const url = isLogin ? `${API_BASE_URL}/authenticate` : `${API_BASE_URL}/create_user`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(isLogin ? { email, password } : { email, password, signupPassword }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = await response.json();

      // Store the token in localStorage
      localStorage.setItem('token', data.token);

      // Store returning user information
      localStorage.setItem('returningUser', 'true');

      if (isLogin) {
        // Use the helper function to check subscription and redirect
        await checkSubscriptionAndRedirect(
          navigate,
          () => navigate('/dashboard'),
          () => navigate('/checkout')
        );
      } else {
        // For new users, redirect directly to the checkout page
        navigate('/checkout');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 px-4 sm:px-6 lg:px-8 pt-20 pb-40">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-md space-y-8 -mt-30">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            {isLogin ? 'Welcome back' : 'Create an account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            {' '}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 focus:outline-none focus:underline transition ease-in-out duration-150"
            >
              {isLogin ? 'Sign Up' : 'Login'}
            </button>
          </p>
        </div>
        { !isLogin && !hideSignupPassword && (
        <div className="flex items-center">
            <label htmlFor="terms-agreement" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
              Registration is currently limited while we are in early testing mode. A Signup Password is required for registration.     
            </label>
        </div>
        )}
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">{error}</h3>
              </div>
            </div>
          </div>
        )}
        <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
          <div>
            <input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="w-full p-3 rounded bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete={isLogin ? "current-password" : "new-password"}
              required
              className="w-full p-3 rounded bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {isLogin && (
              <div className="text-sm text-right mt-1">
                <Link 
                  to="/forgot-password" 
                  className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  Forgot password?
                </Link>
              </div>
            )}
          </div>
          {!isLogin && (
            <>
              <div>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="w-full p-3 rounded bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              {!hideSignupPassword && (
                <div>
                  <input
                    id="signup-password"
                    name="signup-password"
                    type="password"
                    required
                    className="w-full p-3 rounded bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Signup Password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                  />
                </div>
              )}
              <div className="flex flex-col">
                <label className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  By using this site, you agree to the <a href="/terms.html" className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">Terms of Service</a>. 
                </label>
                <label className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Also, see our  <a href="/privacy.html" className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">Privacy Policy</a>.
                  </label> 
              </div>
            </>
          )}
          <div>
            <button
              type="submit"
              className="w-full p-3 rounded bg-indigo-600 text-white font-bold hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            >
              {isLogin ? 'Sign in' : 'Sign up and Agree to Terms of Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AuthPage;
